import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit, Save, X } from 'lucide-react';
import AddItemModal from '../../components/AddItemModal';
import {
    fetchChecklistTemplate,
    createMasterChecklistItem,
    updateMasterChecklistItem,
    deleteMasterChecklistItem,
    fetchDropdownOptions
} from '../../services/apiService';

const AdminChecklistPage = () => {
    const [template, setTemplate] = useState({ sales: [], service: [] });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentType, setCurrentType] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editedItem, setEditedItem] = useState(null);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        loadTemplate();
        loadCategories();
    }, []);

    const loadTemplate = async () => {
        try {
            setLoading(true);
            const data = await fetchChecklistTemplate();
            setTemplate(data);
        } catch (error) {
            console.error('Error loading checklist template:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const options = await fetchDropdownOptions();
            setCategories(options.checklistCategories || []);
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    };

    const handleDelete = async (type, item) => {
        try {
            setSaving(true);
            await deleteMasterChecklistItem(item.id);
            await loadTemplate(); // Reload from database
        } catch (error) {
            console.error('Error deleting checklist item:', error);
            alert('Failed to delete checklist item. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdate = async (type, updatedItem) => {
        try {
            setSaving(true);
            await updateMasterChecklistItem(updatedItem.id, {
                category: updatedItem.category,
                task: updatedItem.task,
                type: updatedItem.type,
                field_type: updatedItem.field_type,
                info: updatedItem.info
            });
            await loadTemplate(); // Reload from database
        } catch (error) {
            console.error('Error updating checklist item:', error);
            alert('Failed to update checklist item. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const openAddItemModal = (type) => {
        setCurrentType(type);
        setIsModalOpen(true);
    };

    const handleAddItem = async (formData) => {
        try {
            setSaving(true);
            const newItem = {
                id: `task-${currentType}-${Date.now()}`,
                task: formData.itemName,
                category: formData.category || 'New Tasks',
                type: currentType,
                field_type: null,
                info: null
            };

            await createMasterChecklistItem(newItem);
            await loadTemplate(); // Reload from database
            setIsModalOpen(false);
        } catch (error) {
            console.error('Error creating checklist item:', error);
            alert('Failed to create checklist item. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const startEditing = (item) => {
        setEditingId(item.id);
        setEditedItem({ ...item });
    };

    const cancelEditing = () => {
        setEditingId(null);
        setEditedItem(null);
    };

    const saveEditing = async (type) => {
        await handleUpdate(type, editedItem);
        cancelEditing();
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditedItem(prev => ({ ...prev, [name]: value }));
    };

    const modalFields = [
        { name: "Task Name", id: "itemName", type: "text" },
        { name: "Category", id: "category", type: "select", options: categories }
    ];

    const renderSection = (type) => (
        <div className="bg-white p-6 rounded-xl shadow-sm border">
            <h3 className="text-xl font-bold capitalize">{type} Checklist</h3>
            <div className="mt-4 space-y-2">
                <div className="grid grid-cols-3 gap-4 text-xs font-bold text-gray-500 uppercase p-2">
                    <span>Task</span>
                    <span>Category</span>
                    <span className="text-right">Actions</span>
                </div>
                {template[type]?.map(item => (
                    <div key={item.id} className="grid grid-cols-3 gap-4 items-center p-2 hover:bg-gray-50 rounded-lg border">
                        {editingId === item.id ? (
                            <>
                                <div>
                                    <input
                                        type="text"
                                        name="task"
                                        value={editedItem.task}
                                        onChange={handleEditChange}
                                        className="w-full p-1 border rounded text-sm"
                                        disabled={saving}
                                    />
                                </div>
                                <div>
                                    <select
                                        name="category"
                                        value={editedItem.category}
                                        onChange={handleEditChange}
                                        className="w-full p-1 border rounded text-sm"
                                        disabled={saving}
                                    >
                                        <option value="">Select Category</option>
                                        {categories.map(category => (
                                            <option key={category} value={category}>{category}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex justify-end items-center space-x-1">
                                    <button
                                        onClick={() => saveEditing(type)}
                                        className="p-2 text-green-600"
                                        disabled={saving}
                                    >
                                        <Save size={16} />
                                    </button>
                                    <button
                                        onClick={cancelEditing}
                                        className="p-2 text-gray-500"
                                        disabled={saving}
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <p className="text-sm">{item.task}</p>
                                <p className="text-sm text-gray-500">{item.category}</p>
                                <div className="flex justify-end items-center space-x-1">
                                    <button
                                        onClick={() => startEditing(item)}
                                        className="p-2 text-gray-500"
                                        disabled={saving}
                                    >
                                        <Edit size={16} />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(type, item)}
                                        className="p-2 text-gray-500"
                                        disabled={saving}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>
            <button
                onClick={() => openAddItemModal(type)}
                className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2"
                disabled={saving}
            >
                <Plus size={18} /> Add {type} Task
            </button>
        </div>
    );

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-gray-500">Loading checklist template...</div>
            </div>
        );
    }

    return (
        <div>
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">Master Checklist Template</h2>
                {saving && (
                    <div className="text-sm text-blue-600">Saving changes...</div>
                )}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-6">
                {renderSection('sales')}
                {renderSection('service')}
            </div>
            <AddItemModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleAddItem}
                title="Add New Task"
                fields={modalFields}
            />
        </div>
    );
};

export default AdminChecklistPage;